import { Film, FileText, Volume2, Subtitles, Download, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

interface PreviewData {
  title: string;
  language: string;
  duration: string;
  hasScript: boolean;
  hasVoice: boolean;
  hasSubtitles: boolean;
}

interface PreviewPanelProps {
  data: PreviewData;
  isGenerating: boolean;
  videoId: string | null;
}

export default function PreviewPanel({
  data,
  isGenerating,
  videoId,
}: PreviewPanelProps) {
  return (
    <Card className="p-6 border-slate-700 bg-slate-800/50 h-full">
      <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
        <Film className="w-5 h-5 text-cyan-400" />
        معاينة فورية
      </h2>

      {isGenerating || !videoId ? (
        <div className="space-y-4">
          {/* Video Preview Skeleton */}
          <div className="bg-slate-900 rounded-lg aspect-video flex items-center justify-center animate-pulse">
            <Film className="w-12 h-12 text-slate-600" />
          </div>

          {/* Preview Details */}
          <div className="space-y-3">
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <p className="text-xs text-slate-400 mb-1">العنوان</p>
              <p className="text-white font-medium text-sm">{data.title}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">اللغة</p>
                <p className="text-white font-medium text-sm">{data.language}</p>
              </div>
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">المدة</p>
                <p className="text-white font-medium text-sm">{data.duration}</p>
              </div>
            </div>

            {/* Feature Status */}
            <div className="space-y-2 pt-2 border-t border-slate-700">
              <div className="flex items-center gap-2">
                <FileText
                  className={`w-4 h-4 ${
                    data.hasScript ? "text-cyan-400" : "text-slate-600"
                  }`}
                />
                <span className="text-sm text-slate-300">السيناريو</span>
              </div>
              <div className="flex items-center gap-2">
                <Volume2
                  className={`w-4 h-4 ${
                    data.hasVoice ? "text-cyan-400" : "text-slate-600"
                  }`}
                />
                <span className="text-sm text-slate-300">الصوت</span>
              </div>
              <div className="flex items-center gap-2">
                <Subtitles
                  className={`w-4 h-4 ${
                    data.hasSubtitles ? "text-cyan-400" : "text-slate-600"
                  }`}
                />
                <span className="text-sm text-slate-300">الترجمات</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-slate-900 rounded-lg aspect-video flex items-center justify-center bg-cover bg-center">
            <div className="text-center">
              <Play className="w-16 h-16 text-cyan-400 mx-auto mb-2 opacity-80" />
              <p className="text-slate-300">الفيلم جاهز للعرض</p>
            </div>
          </div>

          <div className="space-y-2">
            <Button className="w-full bg-cyan-500 hover:bg-cyan-600 text-white h-10">
              <Play className="w-4 h-4 mr-2" />
              تشغيل
            </Button>
            <Button className="w-full bg-slate-700 hover:bg-slate-600 text-white h-10">
              <Download className="w-4 h-4 mr-2" />
              تحميل الفيلم
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
